import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Box, Button, Chip, Grid, List, ListItem, Skeleton, Stack, Typography } from '@mui/material';
import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { useMaps } from '/@/hooks/maps';
import { useHasWriteAccess } from '/@/hooks/session';
import { _ } from '/@/locale';
import { getSessionCountry } from '/@/store/session/selectors';
import { mpTrackEvent } from '/@/store/tracking/mixpanel-events';
import type { CropType } from '/@/types/crop-monitoring';
import type { Field } from '/@/types/field';
import { reverse } from '/@/utils/router';
import CropMonitoringDetailCard from '../detail-card';

/** Backend map types prefixed with `corn_` apply only to corn (see MAP_TYPE_CHOICES). */
function isMapTypeRelevantForCrop(mapType: string, crop: CropType | undefined): boolean {
    if (!crop) return true;
    if (crop === 'corn') return true;
    return !mapType.startsWith('corn_');
}

const getMergedMapsList = ({ mapValues, maps = [] }) =>
    mapValues
        .map((mapValue) => {
            const matchingMap = maps.find((map) => map.id === mapValue.map);

            if (matchingMap) {
                return {
                    type: mapValue.type,
                    riskLevel: mapValue.risk,
                    name: matchingMap.name,
                    scientificName: matchingMap.scientificName,
                    versionLabel: matchingMap.versionLabel,
                };
            }
            return null;
        })
        .filter((item) => item !== null);

function AlertsAndRisksList({ mergedMapsList }) {
    const groupedAlertsAndRisks = mergedMapsList.reduce((acc, item) => {
        if (!acc[item.type]) {
            acc[item.type] = [];
        }
        acc[item.type].push({
            name: item.name,
            scientificName: item.scientificName,
            riskLevel: item.riskLevel,
            versionLabel: item.versionLabel,
        });
        return acc;
    }, {});

    const getRiskLevelData = (level: 'medium' | 'high') => {
        return {
            high: {
                text: _('Alto'),
                backgroundColor: '#ffebee',
                color: '#c62828',
            },
            medium: {
                text: _('Medio'),
                backgroundColor: '#fff8e1',
                color: '#f9a825',
            },
        }[level];
    };

    const getRiskTitle = (title: 'corn_diseases_historic_pressure' | 'corn_pests_historic_pressure' | 'weeds_rem') => {
        return {
            corn_diseases_historic_pressure: _('Riesgos de enfermedades'),
            corn_pests_historic_pressure: _('Riesgos de pestes'),
            weeds_rem: _('Riesgos de maleza'),
        }[title];
    };

    return (
        <Box sx={{ flexGrow: 1, overflow: 'auto', p: 2 }}>
            {Object.entries(groupedAlertsAndRisks).map(([title, diseases], index) => (
                <React.Fragment key={title}>
                    <Typography
                        color='primary'
                        variant='subtitle2'
                        sx={[
                            {
                                fontWeight: 'bold',
                                minHeight: '40px',
                                alignContent: 'center',
                            },
                            index > 0 && { borderTop: (t) => `1px ${t.palette.divider} solid` },
                        ]}
                    >
                        {getRiskTitle(title)}
                    </Typography>
                    <List
                        sx={{
                            p: 0,
                        }}
                    >
                        {diseases.map((disease, index) => (
                            <ListItem
                                key={index}
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'stretch',
                                    p: 1,
                                }}
                            >
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        width: '100%',
                                        alignItems: 'flex-start',
                                    }}
                                >
                                    <Box>
                                        <Stack spacing={1} direction='row' alignItems='center'>
                                            <Typography
                                                color='primary'
                                                variant='subtitle1'
                                                sx={{
                                                    fontWeight: 'bold',
                                                }}
                                            >
                                                {_(disease.name)}
                                            </Typography>
                                            <Typography color='primary.mui.inactive' variant='caption'>
                                                ({_(disease.versionLabel)})
                                            </Typography>
                                        </Stack>
                                        <Typography color='primary.mui.inactive' variant='subtitle2'>
                                            {disease.scientificName}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                                        <Chip
                                            label={getRiskLevelData(disease.riskLevel).text}
                                            sx={{
                                                ...getRiskLevelData(disease.riskLevel),
                                                fontSize: (t) => t.typography.caption.fontSize,
                                                borderRadius: '16px',
                                                fontWeight: 700,
                                                mb: 1,
                                            }}
                                        />
                                    </Box>
                                </Box>
                            </ListItem>
                        ))}
                    </List>
                </React.Fragment>
            ))}
        </Box>
    );
}

function AlertsAndRisksNoList() {
    return (
        <Box sx={{ flexGrow: 1, display: 'flex', p: 2, alignItems: 'center', justifyContent: 'center' }}>
            <Grid container direction='column' alignItems='center' spacing={2}>
                <Grid item>
                    <InfoOutlinedIcon
                        color='primary'
                        sx={{
                            fontSize: 'xx-large',
                        }}
                    />
                </Grid>
                <Grid item>
                    <Typography
                        color='primary'
                        variant='subtitle1'
                        sx={{
                            fontWeight: 'bold',
                            textAlign: 'center',
                        }}
                    >
                        {_('No se detectaron riesgos en la zona seleccionada')}
                    </Typography>
                </Grid>
                <Grid item>
                    <Typography
                        color='primary.mui.inactive'
                        variant='subtitle1'
                        sx={{
                            textAlign: 'center',
                        }}
                    >
                        {_('Por el momento, no hay riesgos en el área seleccionada.')}
                    </Typography>
                </Grid>
            </Grid>
        </Box>
    );
}

function AlertsAndRisksFooter({ field, crop }: { field: Field; crop?: CropType }) {
    const hasWriteAccess = useHasWriteAccess();
    const navigate = useNavigate();
    function onClickObtainRecommendation() {
        mpTrackEvent('Crop Protection Recommendation began', {
            entry_source: 'crop-monitoring-alerts-and-risks-footer',
        });
        navigate(
            reverse('cropProtectionRecommendation', {
                qs: {
                    fieldId: field?.id,
                    crop,
                    plantingDate: field.activeCropMonitoring?.plantingDate,
                    userNodeId: field.userNode,
                },
            })
        );
    }

    if (!crop || !['corn', 'soy'].includes(crop)) {
        return null;
    }
    return (
        <Grid item padding='16px' sx={{ borderTop: '1px solid #e5e7eb' }}>
            <Button onClick={onClickObtainRecommendation} color='primary' variant='text' disabled={!hasWriteAccess}>
                {_('Obtener una recomendación')}
            </Button>
        </Grid>
    );
}

export default function AlertsAndRisks({ field, crop }: { field: Field; crop?: CropType }) {
    const sessionCountry = useSelector(getSessionCountry);
    const { data: maps, isValidating: loadingMaps } = useMaps({ country: sessionCountry.code });
    const filteredMapValues = useMemo(() => {
        const byRisk =
            field?.mapValues?.filter((mapValue) => mapValue.risk === 'medium' || mapValue.risk === 'high') || [];
        return byRisk.filter((mapValue) => isMapTypeRelevantForCrop(mapValue.type, crop));
    }, [field?.mapValues, crop]);
    const mergedMapsList = useMemo(
        () => getMergedMapsList({ mapValues: filteredMapValues, maps }),
        [filteredMapValues, maps]
    );

    return (
        <CropMonitoringDetailCard
            title={_('Alertas y riesgos')}
            subtitle={_('Probabilidad de incidencias')}
            sx={{ maxHeight: 586, justifyContent: 'space-between' }}
        >
            <Stack
                height='100%'
                direction='column'
                data-testid='alerts-and-risks-stack'
                sx={{
                    overflow: 'hidden',
                }}
            >
                {loadingMaps ? (
                    <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
                        <Skeleton variant='rectangular' width='100%' height='100%' />
                    </Box>
                ) : !mergedMapsList.length ? (
                    <AlertsAndRisksNoList />
                ) : (
                    <AlertsAndRisksList mergedMapsList={mergedMapsList} />
                )}
                <AlertsAndRisksFooter field={field} crop={crop} />
            </Stack>
        </CropMonitoringDetailCard>
    );
}
